/**
 * /frontend/src/features/analytics/pages/AppointmentAnalyticsPage.js
 */

import React, { useEffect, useMemo, useState } from 'react';
import useAppointmentAnalytics from '../hooks/useAppointmentAnalytics';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import GranularitySelector from '../components/shared/GranularitySelector';
import AppointmentKPIs from '../components/appointments/AppointmentKPIs';
import BookingVolumeTrendChart from '../components/appointments/BookingVolumeTrendChart';
import AppointmentOutcomePieChart from '../components/appointments/AppointmentOutcomePieChart';
import ServiceTypeDistribution from '../components/appointments/ServiceTypeDistribution';
import PopularTimeSlotsChart from '../components/appointments/PopularTimeSlotsChart';
import AppointmentVsWalkInChart from '../components/appointments/AppointmentVsWalkInChart';
import {
  toAppointmentOutcomePieSeries,
  toAppointmentServicePieSeries,
  toAppointmentTrendSeries,
  toPopularSlotsSeries
} from '../utils/chartDataTransformers';
import './AppointmentAnalyticsPage.css';

const GRANULARITIES = ['HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY'];

/**
 * Appointment analytics page.
 * @param {{request:Function,storeToken:string,storeUuid:string,startDate:string,endDate:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const AppointmentAnalyticsPage = ({ request, storeToken, storeUuid, startDate, endDate, refreshSignal, onSyncingChange }) => {
  const [granularity, setGranularity] = useState('DAILY');
  const hook = useAppointmentAnalytics({ request, storeToken, storeUuid, startDate, endDate, granularity, refreshSignal });
  const summary = hook.data?.summary || null;
  const trend = hook.data?.trend || null;
  const popularSlots = hook.data?.popularSlots || null;
  const byService = hook.data?.byService || null;
  const syncing = hook.isLoading || hook.isRefreshing;

  const trendSeries = useMemo(() => toAppointmentTrendSeries(trend), [trend]);
  const outcomeSeries = useMemo(() => toAppointmentOutcomePieSeries(summary), [summary]);
  const serviceSeries = useMemo(() => toAppointmentServicePieSeries(byService), [byService]);
  const slotSeries = useMemo(() => toPopularSlotsSeries(popularSlots), [popularSlots]);
  const isEmpty = useMemo(() => !summary && !trend && !popularSlots && !byService, [byService, popularSlots, summary, trend]);

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No appointment analytics data for the selected period." />;

  return (
    <div className="analytics-page-stack">
      <div className="analytics-page-grid-2">
        <AppointmentKPIs summary={summary || {}} />
        <GranularitySelector value={granularity} options={GRANULARITIES} onChange={setGranularity} />
      </div>
      <BookingVolumeTrendChart data={trendSeries} />
      <div className="analytics-page-grid-2">
        <AppointmentOutcomePieChart data={outcomeSeries} />
        <ServiceTypeDistribution data={serviceSeries} />
      </div>
      <div className="analytics-page-grid-2">
        <PopularTimeSlotsChart data={slotSeries} />
        <AppointmentVsWalkInChart data={trendSeries} />
      </div>
    </div>
  );
};

export default AppointmentAnalyticsPage;
